/**
 * YAML → TOML Tool
 *
 * Converts YAML into TOML.
 */

import { stringify as stringifyToml } from 'smol-toml';
import { parse as parseYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const YamlToTomlTool: ITransformerTool = {
  id: 'yaml-to-toml',
  name: 'YAML → TOML',
  description: 'Convert YAML to TOML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['yaml', 'toml', 'convert', 'transform', 'config'],

  seo: {
    slug: 'yaml-to-toml-converter',
    pageTitle: 'YAML to TOML Converter - Free Online Tool | Prism',
    metaDescription: 'Convert YAML to TOML configuration format instantly. Handles nested structures and all common YAML types. Fast, private, runs in your browser.',
    keywords: ['yaml to toml', 'yaml converter', 'convert yaml to toml online', 'yaml toml tool'],
    relatedTools: ['toml-to-yaml', 'yaml-to-json', 'toml-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.YAML],
  outputType: SEMANTIC_TYPES.TOML,

  attributes: [
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'YAML') return false;
    try {
      parseYaml(input);
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseYaml(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid YAML: ${error.message}`, {
        suggestion: 'Check indentation and ensure mappings use "key: value" syntax',
      });
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw ToolError.validation('TOML requires a top-level object (table). Arrays and primitives are not valid TOML root values.', {
        suggestion: 'Wrap your data in a YAML mapping, e.g. key: value',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    try {
      return stringifyToml(parsed as Record<string, unknown>);
    } catch (e) {
      const error = e as Error;
      throw ToolError.execution(`Failed to stringify TOML: ${error.message}`);
    }
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
